"use client"

import Image from "next/image"
import Link from "next/link"
import { cn } from "@/utils/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"

import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useLoginMutation } from "@/hooks/useAuthQuery"
import { LogInIcon } from "lucide-react"
import { LoadingButton } from "@/components/common/loading-button"
import { ServerStatus } from "@/features/auth/components/server-status"

const loginSchema = z.object({
  email: z.string().email("E-mail inválido"),
  password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { mutate: loginMutation, isPending, error } = useLoginMutation()

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const testUsers = [
    {
      label: 'CFC',
      name: process.env.NEXT_PUBLIC_TEST_USER_CFC_NAME,
      email: process.env.NEXT_PUBLIC_TEST_USER_CFC_EMAIL,
      password: process.env.NEXT_PUBLIC_TEST_USER_CFC_PASSWORD,
    },
    {
      label: 'Autonomo',
      name: process.env.NEXT_PUBLIC_TEST_USER_AUTONOMO_NAME,
      email: process.env.NEXT_PUBLIC_TEST_USER_AUTONOMO_EMAIL,
      password: process.env.NEXT_PUBLIC_TEST_USER_AUTONOMO_PASSWORD,
    },
  ].filter(
    (
      user,
    ): user is {
      label: string
      name: string | undefined
      email: string
      password: string
    } => Boolean(user.email && user.password),
  )

  function onSubmit(data: LoginFormData) {
    loginMutation(data)
  }

  function handleTestLogin(email: string, password: string) {
    loginMutation({ email, password })
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form className="p-6 md:p-8" onSubmit={form.handleSubmit(onSubmit)}>
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">Bem-vindo de volta</h1>
                <p className="text-balance text-muted-foreground">
                  Entre na sua conta
                </p>
              </div>
              <Field>
                <FieldLabel htmlFor="email">E-mail</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  placeholder="nome@exemplo.com"
                  {...form.register("email")}
                />
                {form.formState.errors.email && (
                  <span className="text-sm text-destructive">
                    {form.formState.errors.email.message}
                  </span>
                )}
              </Field>
              <Field>
                <div className="flex items-center">
                  <FieldLabel htmlFor="password">Senha</FieldLabel>
                  <Link
                    href="/forgot-password"
                    className="ml-auto text-sm underline-offset-2 hover:underline"
                  >
                    Esqueceu sua senha?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  {...form.register("password")}
                />
                {form.formState.errors.password && (
                  <span className="text-sm text-destructive">
                    {form.formState.errors.password.message}
                  </span>
                )}
                {!!error && (
                  <span className="text-sm text-destructive">
                    {(error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Credenciais inválidas'}
                  </span>
                )}
              </Field>
              {testUsers.length > 0 && (
                <Field>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {testUsers.map((user) => (
                      <Button
                        key={user.label}
                        variant="secondary"
                        type="button"
                        className="w-full"
                        disabled={isPending}
                        title={`${user.name ?? user.label} · ${user.email}`}
                        onClick={() => handleTestLogin(user.email, user.password)}
                      >
                        <LogInIcon data-icon="inline-start" /> Entrar como {user.label}
                      </Button>
                    ))}
                  </div>
                </Field>
              )}
              <Field>
                <LoadingButton type="submit" loading={isPending}>
                  <LogInIcon data-icon="inline-start" /> Entrar
                </LoadingButton>
              </Field>
              <FieldDescription className="text-center">
                Não tem uma conta? <Link href="/register">Cadastre-se</Link>
              </FieldDescription>
            </FieldGroup>
          </form>
          <div className="relative hidden bg-muted md:block">
            <Image
              src="/login-hero.jpg"
              alt="Login hero image"
              fill
              className="object-cover dark:brightness-[0.6]"
              sizes="(min-width: 768px) 50vw, 100vw"
            />
          </div>
        </CardContent>
      </Card>
      <ServerStatus />
      <FieldDescription className="px-6 text-center">
        Ao continuar, você concorda com nossos <a href="#">Termos de Serviço</a>{" "}
        e <a href="#">Política de Privacidade</a>.
      </FieldDescription>
    </div>
  )
}
